import React, { useMemo, useState, memo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Step, { type SortOrder } from "./Step";
import StepDetail from "./StepDetail";
import type { PipelineStage } from "@/hooks/usePipelinesSettings";
import type { Opportunity } from "@/hooks/useOpportunities";
import type { OpportunityFilters, OpportunityStatusFilter } from "../ui/shared/MoreFilterCRM";
import {
  DEFAULT_CRM_DATE_FIELD,
  getOpportunityFilterDate,
} from "@/lib/crmDateFilter";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  rectIntersection,
  type CollisionDetection,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { DeleteCardDialog } from "./DeleteCardDialog";
import { EditOpportunityFullDialog, type OpportunityEditData } from "./EditOpportunityFullDialog";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useCurrency } from "@/hooks/useCurrency";
import { formatDateInAccountTz } from "@/lib/accountTimezone";
import type { ContactOpportunity } from "@/hooks/useContactDetails";
import { useTranslation } from "react-i18next";

interface CrmMainProps {
  stages: PipelineStage[];
  opportunities: Opportunity[];
  loading: boolean;
  onMoveOpportunity: (opportunityId: string, newStageId: string, targetStage?: PipelineStage, orderIndex?: number) => Promise<boolean>;
  onDeleteOpportunity: (opportunityId: string) => Promise<boolean>;
  onUpdateOpportunity: (id: string, updates: Partial<Opportunity>) => Promise<boolean>;
  onReorderOpportunities: (items: { id: string; order_index: number }[]) => Promise<boolean>;
  selectedPipelineId: string;
  searchTerm?: string;
  filters?: OpportunityFilters;
  statusFilter?: OpportunityStatusFilter;
}

const toContactOpportunity = (opp: Opportunity): ContactOpportunity => ({
  id: opp.id,
  pipeline_id: opp.pipeline_id,
  pipeline_stage_id: opp.stage_id,
  title: opp.title,
  product: opp.product,
  price: opp.price,
  tags: opp.tags,
  status: opp.status,
  sale_date: opp.sale_date,
  won_by_user_id: opp.won_by_user_id,
  lost_by_user_id: opp.lost_by_user_id,
  notes: opp.notes,
  closed_at: opp.closed_at,
  loss_reason_id: opp.loss_reason_id,
  loss_reason_name: opp.loss_reason_name,
  created_at: opp.created_at,
  updated_at: opp.updated_at,
});

const COLUMN_WIDTH = 260;
const COLUMN_GAP = 20;
const VISIBLE_CARDS_LIMIT = 50;

const SkeletonCard = () => (
  <div className="bg-white border border-border rounded-2xl p-4 animate-pulse shadow-sm dark:bg-neutral-800 dark:border-transparent dark:shadow-none">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-card-muted dark:bg-neutral-600 rounded-full" />
      <div className="flex-1">
        <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded mb-2 w-3/4" />
        <div className="h-3 bg-card-muted dark:bg-neutral-600 rounded w-1/2" />
      </div>
    </div>
    <div className="space-y-2">
      <div className="h-3 bg-card-muted dark:bg-neutral-600 rounded w-full" />
      <div className="h-3 bg-card-muted dark:bg-neutral-600 rounded w-2/3" />
    </div>
    <div className="flex justify-between mt-3">
      <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded w-20" />
      <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded w-16" />
    </div>
  </div>
);

const SkeletonColumn = ({ width }: { width: number }) => (
  <div className="flex flex-col flex-shrink-0" style={{ width: `${width}px` }}>
    <div className="rounded-xl px-4 py-4 animate-pulse">
      <div className="flex justify-between items-center mb-2">
        <div className="h-5 bg-card-muted dark:bg-neutral-600 rounded w-24" />
        <div className="w-6 h-6 bg-card-muted dark:bg-neutral-600 rounded" />
      </div>
      <div className="flex justify-between items-center">
        <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded w-12" />
        <div className="h-4 bg-card-muted dark:bg-neutral-600 rounded w-16" />
      </div>
    </div>
    <div className="rounded-lg min-h-[300px] p-3 mt-3 space-y-4">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  </div>
);

function sortOpportunities(opps: Opportunity[], sortOrder: SortOrder): Opportunity[] {
  return [...opps].sort((a, b) => {
    if (a.order_index !== b.order_index) return a.order_index - b.order_index;
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });
}

function findStageForItem(
  itemId: string,
  opportunitiesByStage: Map<string, Opportunity[]>
): string | null {
  for (const [stageId, opps] of opportunitiesByStage) {
    if (opps.some(o => o.id === itemId)) return stageId;
  }
  return null;
}

const customCollisionDetection: CollisionDetection = (args) => {
  const cornersResult = closestCorners(args);
  if (cornersResult.length > 0) return cornersResult;
  return rectIntersection(args);
};

const CrmMain: React.FC<CrmMainProps> = ({
  stages,
  opportunities,
  loading,
  onMoveOpportunity,
  onDeleteOpportunity,
  onUpdateOpportunity,
  onReorderOpportunities,
  selectedPipelineId,
  searchTerm = "",
  filters,
  statusFilter = 'open'
}) => {
  const { t } = useTranslation("crm");
  const { formatCurrency } = useCurrency();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [opportunityToDelete, setOpportunityToDelete] = useState<Opportunity | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [opportunityToEdit, setOpportunityToEdit] = useState<Opportunity | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [overStageId, setOverStageId] = useState<string | null>(null);
  const sortOrdersRef = useRef<Map<string, SortOrder>>(new Map());
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const floatingScrollRef = useRef<HTMLDivElement>(null);
  const isSyncingScrollRef = useRef(false);
  const [floatingBarLayout, setFloatingBarLayout] = useState({
    visible: false,
    left: 0,
    width: 0,
    bottom: 0,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const allStagesForDisplay = useMemo(() => {
    return [...stages].sort((a, b) => a.order_index - b.order_index);
  }, [stages]);

  const filteredOpportunities = useMemo(() => {
    let filtered = [...opportunities];

    if (searchTerm && searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(opp => {
        const title = opp.title.toLowerCase();
        const product = (opp.product || '').toLowerCase();
        const contactName = (opp.contact_name || '').toLowerCase();
        const contactPhone = (opp.contact_phone || '').toLowerCase();
        const contactEmail = (opp.contact_email || '').toLowerCase();
        return title.includes(search) || product.includes(search) ||
               contactName.includes(search) || contactPhone.includes(search) ||
               contactEmail.includes(search);
      });
    }

    if (filters) {
      if (filters.tags.length > 0 && filters.tags[0] !== '__clear__') {
        filtered = filtered.filter(opp => {
          const oppTags = opp.tags || [];
          const cTags = opp.contact_tags || [];
          const allTags = [...oppTags, ...cTags].map(t => t.toLowerCase());
          if (allTags.length === 0) return false;
          return filters.tags.some(filterTag => allTags.includes(filterTag.toLowerCase()));
        });
      }

      if (filters.minPrice !== null) {
        filtered = filtered.filter(opp => opp.price >= filters.minPrice!);
      }

      if (filters.maxPrice !== null) {
        filtered = filtered.filter(opp => opp.price <= filters.maxPrice!);
      }

      if (filters.attributionId) {
        if (filters.attributionId === '__unassigned__') {
          filtered = filtered.filter(opp => !opp.attributed_user_id);
        } else {
          filtered = filtered.filter(opp => opp.attributed_user_id === filters.attributionId);
        }
      }

      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        const dateField = filters.dateField || DEFAULT_CRM_DATE_FIELD;
        filtered = filtered.filter(opp => {
          const oppDate = getOpportunityFilterDate(opp, dateField);
          if (!oppDate) return false;
          oppDate.setHours(0, 0, 0, 0);
          return oppDate >= fromDate;
        });
      }

      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        const dateField = filters.dateField || DEFAULT_CRM_DATE_FIELD;
        filtered = filtered.filter(opp => {
          const oppDate = getOpportunityFilterDate(opp, dateField);
          if (!oppDate) return false;
          return oppDate <= toDate;
        });
      }
    }

    return filtered;
  }, [opportunities, searchTerm, filters]);

  const opportunitiesByStage = useMemo(() => {
    const grouped = new Map<string, Opportunity[]>();
    allStagesForDisplay.forEach((stage) => {
      grouped.set(stage.id, []);
    });
    filteredOpportunities.forEach((opp) => {
      const stageOpps = grouped.get(opp.stage_id);
      if (stageOpps) {
        stageOpps.push(opp);
      }
    });
    return grouped;
  }, [allStagesForDisplay, filteredOpportunities]);

  const stageTotals = useMemo(() => {
    const totals = new Map<string, { count: number; amount: number }>();
    allStagesForDisplay.forEach((stage) => {
      const opps = opportunitiesByStage.get(stage.id) || [];
      const amount = opps.reduce((sum, opp) => sum + (opp.price || 0), 0);
      totals.set(stage.id, { count: opps.length, amount });
    });
    return totals;
  }, [allStagesForDisplay, opportunitiesByStage]);

  const handleSortOrderChange = useCallback((stageId: string, order: SortOrder) => {
    sortOrdersRef.current.set(stageId, order);
  }, []);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    if (!over) {
      setOverStageId(null);
      return;
    }

    const overId = String(over.id);
    if (overId.startsWith("droppable-")) {
      setOverStageId(overId.replace("droppable-", ""));
    } else {
      const stageId = findStageForItem(overId, opportunitiesByStage);
      setOverStageId(stageId);
    }
  }, [opportunitiesByStage]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverStageId(null);

    if (!over || active.id === over.id) return;

    const opportunityId = String(active.id);
    const overId = String(over.id);

    const activeOpp = filteredOpportunities.find(o => o.id === opportunityId);
    if (!activeOpp) return;

    const isOverADroppable = overId.startsWith("droppable-");
    const overOpp = !isOverADroppable ? filteredOpportunities.find(o => o.id === overId) : null;

    const targetStageId = isOverADroppable
      ? overId.replace("droppable-", "")
      : overOpp?.stage_id;

    if (!targetStageId) return;

    const isSameStage = activeOpp.stage_id === targetStageId;

    if (isSameStage) {
      if (!overOpp) return;
      const stageOpps = opportunitiesByStage.get(targetStageId) || [];
      const sortOrder = sortOrdersRef.current.get(targetStageId) || 'newest';
      const sorted = sortOpportunities(stageOpps, sortOrder);

      const oldIndex = sorted.findIndex(o => o.id === opportunityId);
      const newIndex = sorted.findIndex(o => o.id === overId);

      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reordered = arrayMove(sorted, oldIndex, newIndex);
      const updates = reordered.map((opp, idx) => ({
        id: opp.id,
        order_index: idx,
      }));

      await onReorderOpportunities(updates);
    } else {
      const targetStage = allStagesForDisplay.find(s => s.id === targetStageId);
      if (!targetStage) return;

      const targetStageOpps = opportunitiesByStage.get(targetStageId) || [];
      const sortOrder = sortOrdersRef.current.get(targetStageId) || 'newest';
      const sorted = sortOpportunities(targetStageOpps, sortOrder);

      let insertIndex: number;
      if (overOpp) {
        const overIndex = sorted.findIndex(o => o.id === overId);
        insertIndex = overIndex !== -1 ? overIndex : sorted.length;
      } else {
        insertIndex = sorted.length;
      }

      const moved = await onMoveOpportunity(opportunityId, targetStageId, targetStage, insertIndex);

      if (moved) {
        const updatedOpps = [...sorted];
        updatedOpps.splice(insertIndex, 0, { ...activeOpp, stage_id: targetStageId });
        const updates = updatedOpps.map((opp, idx) => ({
          id: opp.id,
          order_index: idx,
        }));
        await onReorderOpportunities(updates);
      }
    }
  }, [allStagesForDisplay, filteredOpportunities, opportunitiesByStage, onMoveOpportunity, onReorderOpportunities]);

  const handleDeleteRequest = useCallback((opportunity: Opportunity) => {
    setOpportunityToDelete(opportunity);
    setDeleteDialogOpen(true);
  }, []);

  const handleEditRequest = useCallback((opportunity: Opportunity) => {
    setOpportunityToEdit(opportunity);
    setEditDialogOpen(true);
  }, []);

  // Objeto estável: recriá-lo a cada render do board fazia o diálogo de edição
  // recarregar os campos por cima do que estava sendo digitado
  const editingContactOpportunity = useMemo(
    () => (opportunityToEdit ? toContactOpportunity(opportunityToEdit) : null),
    [opportunityToEdit]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!opportunityToDelete) return;
    setIsDeleting(true);
    await onDeleteOpportunity(opportunityToDelete.id);
    setIsDeleting(false);
    setDeleteDialogOpen(false);
    setOpportunityToDelete(null);
  }, [opportunityToDelete, onDeleteOpportunity]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setOpportunityToDelete(null);
  }, []);

  // O diálogo de edição já coleta motivo da perda, data da venda e responsável, então
  // a gravação acontece de uma vez só — sem um segundo pop-up repetindo os campos
  const handleSaveOpportunityEdit = useCallback(
    async (data: OpportunityEditData) => {
      if (!opportunityToEdit) return false;

      const updates: Partial<Opportunity> = {
        title: data.title,
        pipeline_id: data.pipeline_id,
        stage_id: data.stage_id,
        product: data.product,
        price: data.price ?? 0,
        status: data.status,
        notes: data.notes ?? null,
      };

      // Cada desfecho limpa os campos do outro, senão um card reaberto e fechado do
      // lado oposto fica com dados contraditórios
      if (data.status === "won") {
        updates.closed_at = data.closed_at || new Date().toISOString();
        updates.sale_date = data.sale_date ?? null;
        updates.won_by_user_id = data.won_by_user_id ?? null;
        updates.loss_reason_id = null;
        updates.lost_by_user_id = null;
      } else if (data.status === "lost") {
        updates.closed_at = data.closed_at || new Date().toISOString();
        updates.loss_reason_id = data.loss_reason_id ?? null;
        updates.lost_by_user_id = data.lost_by_user_id ?? null;
        updates.sale_date = null;
        updates.won_by_user_id = null;
      } else {
        updates.closed_at = null;
        updates.sale_date = null;
        updates.won_by_user_id = null;
        updates.lost_by_user_id = null;
        updates.loss_reason_id = null;
      }

      // Falhou (permissão, rede, banco): o diálogo fica aberto com o que foi digitado
      // e o erro chega ao usuário pelo toast de updateOpportunity
      const saved = await onUpdateOpportunity(opportunityToEdit.id, updates);
      if (!saved) return false;

      setEditDialogOpen(false);
      setOpportunityToEdit(null);
      return true;
    },
    [opportunityToEdit, onUpdateOpportunity]
  );

  const activeOpportunity = filteredOpportunities.find((opp) => opp.id === activeId);

  const hasActiveFilters = searchTerm.trim() || (filters && (
    filters.tags.length > 0 ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.dateFrom ||
    filters.dateTo ||
    !!filters.attributionId
  ));

  const totalWidth = allStagesForDisplay.length * COLUMN_WIDTH + (allStagesForDisplay.length - 1) * COLUMN_GAP + 32;

  useEffect(() => {
    const real = horizontalScrollRef.current;
    if (!real) return;

    const updateLayout = () => {
      const rect = real.getBoundingClientRect();
      const needsScroll = real.scrollWidth > real.clientWidth + 1;
      const isInViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      const visible = needsScroll && isInViewport;

      if (!visible) {
        setFloatingBarLayout((prev) => (prev.visible ? { ...prev, visible: false } : prev));
        return;
      }

      const bottom = Math.max(window.innerHeight - rect.bottom, 0);
      setFloatingBarLayout({
        visible: true,
        left: rect.left,
        width: rect.width,
        bottom,
      });
    };

    const rafUpdate = () => requestAnimationFrame(updateLayout);

    rafUpdate();

    const resizeObserver = new ResizeObserver(rafUpdate);
    resizeObserver.observe(real);
    resizeObserver.observe(document.body);

    window.addEventListener("resize", rafUpdate);
    window.addEventListener("scroll", rafUpdate, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", rafUpdate);
      window.removeEventListener("scroll", rafUpdate, true);
    };
  }, [totalWidth, allStagesForDisplay.length, opportunities.length]);

  useEffect(() => {
    const real = horizontalScrollRef.current;
    const fake = floatingScrollRef.current;
    if (!real || !fake) return;

    const handleRealScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      fake.scrollLeft = real.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    const handleFakeScroll = () => {
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      real.scrollLeft = fake.scrollLeft;
      requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };

    real.addEventListener("scroll", handleRealScroll, { passive: true });
    fake.addEventListener("scroll", handleFakeScroll, { passive: true });

    return () => {
      real.removeEventListener("scroll", handleRealScroll);
      fake.removeEventListener("scroll", handleFakeScroll);
    };
  }, [floatingBarLayout.visible]);

  if (loading && opportunities.length === 0) {
    const skeletonCount = Math.max(allStagesForDisplay.length, 4);
    const skeletonWidth = skeletonCount * COLUMN_WIDTH + (skeletonCount - 1) * COLUMN_GAP + 32;
    return (
      <div className="crm-horizontal-scroll overflow-x-auto pb-4">
        <div className="flex gap-4 pb-2" style={{ minWidth: `${skeletonWidth}px` }}>
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <SkeletonColumn key={i} width={COLUMN_WIDTH} />
          ))}
        </div>
      </div>
    );
  }

  if (!selectedPipelineId || stages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground dark:text-gray-400">{t("board.noPipeline")}</div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-4">
        {hasActiveFilters && (
          <div className="text-sm text-muted-foreground dark:text-gray-400">
            {t("board.showingOf", { shown: filteredOpportunities.length, total: opportunities.length })}
          </div>
        )}

        <div
          ref={horizontalScrollRef}
          className="crm-horizontal-scroll overflow-x-auto pb-2"
        >
          <div
            className="flex gap-4 pb-2"
            style={{ minWidth: `${totalWidth}px` }}
          >
            {allStagesForDisplay.map((stage) => {
              const totals = stageTotals.get(stage.id) || { count: 0, amount: 0 };
              const stageOpportunities = opportunitiesByStage.get(stage.id) || [];

              return (
                <StageColumn
                  key={stage.id}
                  stage={stage}
                  totals={totals}
                  opportunities={stageOpportunities}
                  activeId={activeId}
                  onDeleteRequest={handleDeleteRequest}
                  onEditRequest={handleEditRequest}
                  statusFilter={statusFilter}
                  columnWidth={COLUMN_WIDTH}
                  isOver={overStageId === stage.id}
                  onSortOrderChange={handleSortOrderChange}
                />
              );
            })}
          </div>
        </div>
      </div>

      {createPortal(
        <div
          style={{
            position: "fixed",
            left: `${floatingBarLayout.left}px`,
            width: `${floatingBarLayout.width}px`,
            bottom: `${floatingBarLayout.bottom}px`,
            zIndex: 40,
            opacity: floatingBarLayout.visible ? 1 : 0,
            pointerEvents: floatingBarLayout.visible ? "auto" : "none",
            transition: "opacity 150ms ease-in-out",
          }}
          aria-hidden={!floatingBarLayout.visible}
        >
          <div
            ref={floatingScrollRef}
            className="crm-floating-scrollbar overflow-x-scroll bg-white/90 backdrop-blur-sm rounded-md shadow-md border border-border dark:bg-neutral-900/90 dark:shadow-lg dark:border-neutral-700/50"
          >
            <div style={{ width: `${totalWidth}px`, height: 1 }} />
          </div>
        </div>,
        document.body
      )}

      <DragOverlay>
        {activeOpportunity && (
          <div style={{ opacity: 0.85, width: COLUMN_WIDTH - 16 }}>
            <StepDetail
              contactId={activeOpportunity.contact_id}
              contactName={activeOpportunity.contact_name || 'Contato sem nome'}
              phone={activeOpportunity.contact_phone || ""}
              tags={activeOpportunity.tags}
              contactTags={activeOpportunity.contact_tags}
              productName={activeOpportunity.product || ""}
              amount={formatCurrency(activeOpportunity.price)}
              createdAt={formatDateInAccountTz(activeOpportunity.created_at)}
              attributedUserId={activeOpportunity.attributed_user_id}
              attributedUserName={activeOpportunity.attributed_user_name}
            />
          </div>
        )}
      </DragOverlay>

      <DeleteCardDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={opportunityToDelete?.contact_name || 'Contato sem nome'}
        isDeleting={isDeleting}
      />

      <EditOpportunityFullDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setOpportunityToEdit(null);
        }}
        onSave={handleSaveOpportunityEdit}
        opportunity={editingContactOpportunity}
      />
    </DndContext>
  );
};

interface StageColumnProps {
  stage: PipelineStage;
  totals: { count: number; amount: number };
  opportunities: Opportunity[];
  activeId: UniqueIdentifier | null;
  onDeleteRequest: (opportunity: Opportunity) => void;
  onEditRequest: (opportunity: Opportunity) => void;
  statusFilter: OpportunityStatusFilter;
  columnWidth: number;
  isOver: boolean;
  onSortOrderChange: (stageId: string, order: SortOrder) => void;
}

const StageColumn: React.FC<StageColumnProps> = memo(({
  stage,
  totals,
  opportunities,
  activeId,
  onDeleteRequest,
  onEditRequest,
  statusFilter,
  columnWidth,
  isOver,
  onSortOrderChange,
}) => {
  const { t } = useTranslation("crm");
  const { formatCurrency } = useCurrency();
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const [visibleCount, setVisibleCount] = useState(VISIBLE_CARDS_LIMIT);

  const handleSortToggle = useCallback(() => {
    setSortOrder(prev => {
      const next = prev === 'newest' ? 'oldest' : 'newest';
      onSortOrderChange(stage.id, next);
      return next;
    });
  }, [onSortOrderChange, stage.id]);

  const sortedOpportunities = useMemo(() => {
    return sortOpportunities(opportunities, sortOrder);
  }, [opportunities, sortOrder]);

  const displayedOpportunities = useMemo(() => {
    return sortedOpportunities.slice(0, visibleCount);
  }, [sortedOpportunities, visibleCount]);

  const sortedIds = useMemo(() =>
    displayedOpportunities.map(o => o.id),
    [displayedOpportunities]
  );

  const hasMoreItems = sortedOpportunities.length > visibleCount;
  const remainingCount = sortedOpportunities.length - visibleCount;

  const handleShowMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + VISIBLE_CARDS_LIMIT, sortedOpportunities.length));
  }, [sortedOpportunities.length]);

  const handleShowLess = useCallback(() => {
    setVisibleCount(VISIBLE_CARDS_LIMIT);
  }, []);

  const getColumnContainerStyle = () => {
    if (stage.stage_type === 'won') {
      return isOver
        ? 'bg-[#ECFDF5] border-2 border-[#16A34A]/50 dark:bg-green-900/30 dark:border-green-500/70'
        : 'bg-[#ECFDF5]/60 border border-[#BBF7D0] dark:bg-green-900/20 dark:border-green-600/30';
    }
    if (stage.stage_type === 'lost') {
      return isOver
        ? 'bg-red-50 border-2 border-red-400/60 dark:bg-red-900/30 dark:border-red-500/70'
        : 'bg-red-50/60 border border-red-200 dark:bg-red-900/20 dark:border-red-600/30';
    }
    return isOver
      ? 'bg-[#EEF4FF] border-2 border-[#2563EB]/40 dark:bg-neutral-700/30 dark:border-neutral-500/50'
      : 'border border-transparent';
  };

  return (
    <div
      className="flex flex-col flex-shrink-0"
      style={{ width: `${columnWidth}px` }}
    >
      <Step
        title={stage.name}
        amount={formatCurrency(totals.amount)}
        count={totals.count}
        stageType={stage.stage_type}
        width={columnWidth}
        sortOrder={sortOrder}
        onSortToggle={handleSortToggle}
      />

      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <DroppableStageArea stageId={stage.id} stageType={stage.stage_type}>
          <div className={`rounded-xl min-h-[300px] p-3 mt-3 transition-all duration-200 ${getColumnContainerStyle()}`}>
            {sortedOpportunities.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground dark:text-neutral-400 py-8">
                <p className="text-sm text-center">{t("board.dragCards")}<br />{t("board.toThisStage")}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {displayedOpportunities.map((opportunity) => {
                  const isDragging = activeId === opportunity.id;
                  return (
                    <SortableOpportunity
                      key={opportunity.id}
                      opportunity={opportunity}
                      isDragging={isDragging}
                      onDeleteRequest={onDeleteRequest}
                      onEditRequest={onEditRequest}
                      isArchived={statusFilter !== 'open'}
                      width={columnWidth - 16}
                    />
                  );
                })}
                {(hasMoreItems || visibleCount > VISIBLE_CARDS_LIMIT) && (
                  <div className="flex gap-2 mt-2 justify-center">
                    {hasMoreItems && (
                      <button
                        onClick={handleShowMore}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 py-1 px-2 rounded-lg hover:bg-[#F1F5F9] dark:text-neutral-400 dark:hover:text-neutral-200 dark:hover:bg-neutral-700"
                      >
                        <ChevronDown className="w-3 h-3" />
                        {t("board.showMoreCount", { count: Math.min(remainingCount, VISIBLE_CARDS_LIMIT) })}
                      </button>
                    )}
                    {visibleCount > VISIBLE_CARDS_LIMIT && (
                      <button
                        onClick={handleShowLess}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-200 py-1 px-2 rounded-lg hover:bg-[#F1F5F9] dark:text-neutral-400 dark:hover:text-neutral-200 dark:hover:bg-neutral-700"
                      >
                        <ChevronUp className="w-3 h-3" />
                        {t("board.collapse")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DroppableStageArea>
      </SortableContext>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.stage.id === nextProps.stage.id &&
    prevProps.totals.count === nextProps.totals.count &&
    prevProps.totals.amount === nextProps.totals.amount &&
    prevProps.opportunities === nextProps.opportunities &&
    prevProps.statusFilter === nextProps.statusFilter &&
    prevProps.columnWidth === nextProps.columnWidth &&
    prevProps.isOver === nextProps.isOver &&
    (prevProps.activeId === nextProps.activeId ||
      (!prevProps.opportunities.some(o => o.id === prevProps.activeId) &&
       !nextProps.opportunities.some(o => o.id === nextProps.activeId)))
  );
});

StageColumn.displayName = 'StageColumn';

interface DroppableStageAreaProps {
  stageId: string;
  stageType?: string;
  children: React.ReactNode;
}

const DroppableStageArea: React.FC<DroppableStageAreaProps> = ({ stageId, children }) => {
  const { t } = useTranslation("crm");
  const { setNodeRef } = useDroppable({
    id: `droppable-${stageId}`,
  });

  return <div ref={setNodeRef}>{children}</div>;
};

interface SortableOpportunityProps {
  opportunity: Opportunity;
  isDragging: boolean;
  onDeleteRequest: (opportunity: Opportunity) => void;
  onEditRequest: (opportunity: Opportunity) => void;
  isArchived: boolean;
  width: number;
}

const SortableOpportunity: React.FC<SortableOpportunityProps> = memo(({
  opportunity,
  isDragging,
  onDeleteRequest,
  onEditRequest,
  isArchived,
  width
}) => {
  const { t } = useTranslation("crm");
  const { formatCurrency } = useCurrency();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isOver: isSortableOver,
  } = useSortable({
    id: opportunity.id,
    data: {
      stageId: opportunity.stage_id,
    },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: "grab",
    width: `${width}px`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`transition-opacity duration-150 ${isSortableOver ? 'border-t-2 border-blue-500/70' : ''}`}
      {...listeners}
      {...attributes}
    >
      <StepDetail
        contactId={opportunity.contact_id}
        contactName={opportunity.contact_name || 'Contato sem nome'}
        phone={opportunity.contact_phone || ""}
        tags={opportunity.tags}
        contactTags={opportunity.contact_tags}
        productName={opportunity.product || ""}
        amount={formatCurrency(opportunity.price)}
        createdAt={formatDateInAccountTz(opportunity.created_at)}
        attributedUserId={opportunity.attributed_user_id}
        attributedUserName={opportunity.attributed_user_name}
        status={opportunity.status}
        onEdit={() => onEditRequest(opportunity)}
        onDelete={() => onDeleteRequest(opportunity)}
        isArchived={isArchived}
      />
    </div>
  );
});

SortableOpportunity.displayName = 'SortableOpportunity';

export default CrmMain;
